import { useState, useEffect } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, 
  DollarSign, 
  Star,
  TrendingUp,
  Clock,
  Video,
  User,
  Settings,
  LogOut,
  Edit,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Users,
  Download,
  FileText,
  Play,
  Wallet,
  Pencil,
  ExternalLink,
  MessageSquare,
  Save,
  Loader2,
  Phone,
  Headphones,
  Bell,
  CalendarPlus,
  Send,
  Check,
  X,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import ExpertLegalRecords from "@/components/ExpertLegalRecords";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ExpertDashboardPage() {
  const navigate = useNavigate();
  const location = useRouterLocation();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming"); // "upcoming" or "past"
  const [expandedSession, setExpandedSession] = useState(null);
  const [expandedUpcoming, setExpandedUpcoming] = useState(null);
  const [downloadingRecording, setDownloadingRecording] = useState(null);
  const [downloadingTranscript, setDownloadingTranscript] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [slotRequests, setSlotRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [proposingSlots, setProposingSlots] = useState(false);
  const [proposedSlots, setProposedSlots] = useState([]);
  const [expertMessage, setExpertMessage] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("UTC");
  const [selectedProposalDate, setSelectedProposalDate] = useState(null);
  
  // Cancel and Reschedule states
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleMessage, setRescheduleMessage] = useState("");
  const [proposingReschedule, setProposingReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);

  // BR-010 — dual-role accounts (experts who ALSO buy reviews from other
  // experts) need to see their customer-side bookings too. Without this,
  // an unpaid booking sits in the DB invisible. Fetched from the same
  // /api/bookings/me endpoint customer DashboardPage uses.
  const [customerBookings, setCustomerBookings] = useState([]);
  const [retryingCustomerPayment, setRetryingCustomerPayment] = useState(null);

  // BR-011 Tranche 1 — pending async reviews assigned to this expert.
  // Without this, the email "New review assignment" lands the user on a
  // dashboard that doesn't show the review they were assigned. The
  // backend orders by due_at asc so the most urgent is row 0.
  const [pendingReviews, setPendingReviews] = useState([]);

  // Timezone options
  const TIMEZONES = [
    { value: "UTC", label: "UTC" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "Europe/London", label: "London (GMT)" },
    { value: "Europe/Paris", label: "Paris (CET)" },
    { value: "Europe/Berlin", label: "Berlin (CET)" },
    { value: "Asia/Dubai", label: "Dubai (GST)" },
    { value: "Asia/Kolkata", label: "India (IST)" },
    { value: "Asia/Singapore", label: "Singapore (SGT)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Australia/Sydney", label: "Sydney (AEDT)" },
  ];

  useEffect(() => {
    fetchDashboard();
    fetchSlotRequests();
    fetchCustomerBookings();
    fetchPendingReviews();
    
    // Check for calendar connection callback
    const params = new URLSearchParams(location.search);
    const calendarStatus = params.get('calendar');
    
    if (calendarStatus === 'connected') {
      toast.success("Google Calendar connected successfully!");
      // Clean up URL
      navigate('/expert-dashboard', { replace: true });
    } else if (calendarStatus === 'error') {
      const message = params.get('message') || 'Unknown error';
      toast.error(`Failed to connect Google Calendar: ${message}`);
      // Clean up URL
      navigate('/expert-dashboard', { replace: true });
    }
    // Re-runs when the URL query string changes (calendar OAuth callback).
    // All four fetchers (fetchDashboard / fetchSlotRequests /
    // fetchCustomerBookings / fetchPendingReviews) are component-local
    // and stable; `navigate` is stable per react-router-v6. Adding them
    // would require useCallback wrapping for no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchSlotRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API}/expert/slot-requests`);
      setSlotRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching slot requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // BR-010 — pull THIS user's own bookings (as customer). Same endpoint
  // and shape DashboardPage uses. We render any non-cancelled bookings,
  // with extra prominence on payment_status === 'pending' (abandoned
  // checkouts) so the user has an obvious path back to Stripe.
  const fetchCustomerBookings = async () => {
    try {
      const r = await axios.get(`${API}/bookings/me`);
      setCustomerBookings(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      // Non-blocking — expert dashboard still loads.
      console.error("Error fetching customer-side bookings:", error);
    }
  };

  // BR-011 Tranche 1 — fetch the expert's open async-review queue.
  // Backend pre-sorts by due_at asc so the dashboard top row is always
  // the most urgent. Returns an empty list (not 404) for non-experts.
  const fetchPendingReviews = async () => {
    try {
      const r = await axios.get(`${API}/verifications/expert-pending`);
      setPendingReviews(r.data?.pending_reviews || []);
    } catch (error) {
      // Non-blocking — dashboard still loads.
      console.error("Error fetching pending reviews:", error);
    }
  };

  const handleCompleteCustomerPayment = async (bookingId) => {
    setRetryingCustomerPayment(bookingId);
    try {
      const r = await axios.post(`${API}/bookings/${bookingId}/retry-payment`, {}, {
        params: { origin_url: window.location.origin },
      });
      const url = r?.data?.checkout_url;
      if (!url) throw new Error("No checkout URL returned.");
      window.location.href = url;
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "Couldn't reach payment.";
      toast.error(typeof detail === "string" ? detail : "Couldn't reach payment.");
      setRetryingCustomerPayment(null);
    }
  };

  const handleProposeSlots = async (requestId) => {
    if (proposedSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }
    
    setProposingSlots(true);
    try {
      await axios.post(`${API}/slot-requests/${requestId}/propose`, {
        proposed_slots: proposedSlots,
        message: expertMessage
      });
      toast.success("Slots proposed successfully! The user has been notified.");
      setSelectedRequest(null);
      setProposedSlots([]);
      setExpertMessage("");
      fetchSlotRequests();
    } catch (error) {
      console.error("Error proposing slots:", error);
      toast.error("Failed to propose slots");
    } finally {
      setProposingSlots(false);
    }
  };

  const addProposedSlot = (date, time, time12h) => {
    const slot = { date, time, time_12h: time12h };
    if (proposedSlots.length >= 6) {
      toast.error("Maximum 6 slots allowed");
      return;
    }
    if (!proposedSlots.find(s => s.date === date && s.time === time)) {
      setProposedSlots([...proposedSlots, slot]);
    }
  };

  const removeProposedSlot = (index) => {
    setProposedSlots(proposedSlots.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-500/20", text: "text-yellow-500", label: "Pending" },
      slots_proposed: { bg: "bg-blue-500/20", text: "text-blue-500", label: "Slots Sent" },
      accepted: { bg: "bg-brand-green/15", text: "text-brand-green", label: "Accepted" },
      payment_pending: { bg: "bg-amber-100", text: "text-amber-600", label: "Payment Pending" },
      more_requested: { bg: "bg-amber-100", text: "text-amber-600", label: "More Requested" }
    };
    return badges[status] || badges.pending;
  };

  // Cancel booking handler (expert cancels - full refund to user)
  const handleCancelBooking = async () => {
    if (!cancelModal) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/bookings/${cancelModal.id}/cancel`, {
        reason: cancelReason
      });
      
      toast.success("Booking cancelled. A full refund has been issued to the client.");
      setCancelModal(null);
      setCancelReason("");
      fetchDashboard();
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setCancelling(false);
    }
  };

  // Request user to reschedule (just notifies user)
  const handleRequestUserReschedule = async (booking) => {
    try {
      await axios.post(`${API}/bookings/${booking.id}/reschedule/request`, {
        reason: "Expert requested reschedule"
      });
      toast.success("Reschedule request sent! The client will be notified to select a new time.");
      fetchDashboard();
    } catch (error) {
      console.error("Error requesting reschedule:", error);
      toast.error(error.response?.data?.detail || "Failed to request reschedule");
    }
  };

  // Propose reschedule slots
  const handleProposeRescheduleSlots = async () => {
    if (!rescheduleModal || rescheduleSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }
    
    setProposingReschedule(true);
    try {
      await axios.post(`${API}/bookings/${rescheduleModal.id}/reschedule/propose`, {
        proposed_slots: rescheduleSlots,
        message: rescheduleMessage
      });
      toast.success("Reschedule slots sent! The client will be notified.");
      setRescheduleModal(null);
      setRescheduleSlots([]);
      setRescheduleMessage("");
      setRescheduleDate(null);
      fetchDashboard();
    } catch (error) {
      console.error("Error proposing reschedule:", error);
      toast.error(error.response?.data?.detail || "Failed to propose reschedule slots");
    } finally {
      setProposingReschedule(false);
    }
  };

  const addRescheduleSlot = (date, time, time12h) => {
    const slot = { date, time, time_12h: time12h };
    if (rescheduleSlots.length >= 6) {
      toast.error("Maximum 6 slots allowed");
      return;
    }
    if (!rescheduleSlots.find(s => s.date === date && s.time === time)) {
      setRescheduleSlots([...rescheduleSlots, slot]);
    }
  };

  const removeRescheduleSlot = (index) => {
    setRescheduleSlots(rescheduleSlots.filter((_, i) => i !== index));
  };

  // Generate available dates (next 14 days, excluding weekends)
  const generateAvailableDates = () => {
    const dates = [];
    const now = new Date();
    for (let day = 1; day <= 14; day++) {
      const date = new Date(now);
      date.setDate(date.getDate() + day);
      if (date.getDay() === 0 || date.getDay() === 6) continue; // Skip weekends
      
      dates.push({
        date: date.toISOString().split('T')[0],
        displayDate: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    return dates;
  };

  // Generate time slots for a selected date
  const generateTimeSlotsForDate = (dateStr) => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const time12h = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ date: dateStr, time, time_12h: time12h });
      }
    }
    return slots;
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/experts/me/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 404) {
        navigate("/become-expert");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const toggleAvailability = async () => {
    try {
      await axios.put(`${API}/experts/me`, {
        available: !dashboard.expert.available
      });
      setDashboard(prev => ({
        ...prev,
        expert: { ...prev.expert, available: !prev.expert.available }
      }));
      toast.success(dashboard.expert.available ? "You are now unavailable" : "You are now available");
    } catch (error) {
      toast.error("Failed to update availability");
    }
  };

  // BR-011 Tranche 5 — vacation/pause mode + weekly capacity. The matcher
  // honours both when routing new async reviews.
  const togglePause = async () => {
    const next = !dashboard.expert.is_paused;
    try {
      await axios.put(`${API}/experts/me`, { is_paused: next });
      setDashboard(prev => ({
        ...prev,
        expert: { ...prev.expert, is_paused: next }
      }));
      toast.success(next
        ? "Paused — you won't be assigned new reviews."
        : "Back on. New reviews can be assigned to you."
      );
    } catch (error) {
      toast.error("Failed to update pause status.");
    }
  };

  const updateWeeklyCapacity = async (newCap) => {
    // Allow 0 (closed via capacity) but not negative.
    const parsed = Math.max(0, parseInt(newCap, 10) || 0);
    try {
      await axios.put(`${API}/experts/me`, { weekly_capacity: parsed });
      setDashboard(prev => ({
        ...prev,
        expert: { ...prev.expert, weekly_capacity: parsed }
      }));
      toast.success("Weekly capacity updated.");
    } catch (error) {
      toast.error("Failed to update capacity.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-brand-green/15 text-brand-green";
      case "pending":
        return "bg-yellow-500/20 text-yellow-500";
      case "cancelled":
        return "bg-red-500/20 text-red-500";
      case "completed":
        return "bg-blue-500/20 text-blue-500";
      default:
        return "bg-slate-500/20 text-slateline-400";
    }
  };

  const handleDownloadRecording = async (bookingId) => {
    setDownloadingRecording(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/recording`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("xpert_token")}` }
      });
      const { download_url, filename } = response.data;
      const link = document.createElement('a');
      link.href = download_url;
      link.setAttribute('download', filename || `session_${bookingId}_recording.mp4`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Recording download started");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(error.response?.data?.detail || "Recording not available for this session");
      } else {
        toast.error("Failed to download recording");
      }
    } finally {
      setDownloadingRecording(null);
    }
  };

  const handleDownloadTranscript = async (bookingId) => {
    setDownloadingTranscript(bookingId);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/transcript`);
      const blob = new Blob([response.data.transcript], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `session_${bookingId}_transcript.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Transcript downloaded successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Transcript not available for this session");
      } else {
        toast.error("Failed to download transcript");
      }
    } finally {
      setDownloadingTranscript(null);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleSaveNotes = async (bookingId) => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/bookings/${bookingId}/notes`, {
        expert_notes: notesText
      });
      // Update local state
      setDashboard(prev => ({
        ...prev,
        bookings: prev.bookings.map(b => 
          b.id === bookingId ? { ...b, expert_notes: notesText } : b
        )
      }));
      setEditingNotes(null);
      toast.success("Notes saved successfully");
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const startEditingNotes = (booking) => {
    setEditingNotes(booking.id);
    setNotesText(booking.expert_notes || "");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const { expert, bookings, stats } = dashboard;
  
  const upcomingBookings = bookings.filter(b => 
    (b.status === "confirmed" || b.status === "pending") && 
    new Date(b.scheduled_date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.payment_status === "paid" && 
    (b.status === "completed" || new Date(b.scheduled_date) < new Date())
  );

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      
      <section className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div 
                className="bg-white border border-slateline-200 p-6 rounded-sm sticky top-28"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {/* Profile Card */}
                <div className="text-center mb-6 pb-6 border-b border-slateline-200">
                  <div className="w-20 h-20 bg-brand-greenPale/60 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-display text-2xl text-brand-green">
                      {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                  <h3 className="font-display text-xl">{expert.name}</h3>
                  <p className="text-sm text-slateline-400">{expert.title}</p>
                  
                  {/* Availability Toggle */}
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <Switch
                      checked={expert.available}
                      onCheckedChange={toggleAvailability}
                      data-testid="availability-toggle"
                    />
                    <Label className={expert.available ? "text-brand-green" : "text-slateline-400"}>
                      {expert.available ? "Available" : "Unavailable"}
                    </Label>
                  </div>

                  {/* BR-011 Tranche 5 — capacity + vacation/pause mode.
                      The matcher honours both. Pause is a soft switch
                      (vacation, family emergency); availability above is
                      the hard "not offering services right now" switch. */}
                  <div className="mt-5 pt-5 border-t border-slateline-200 text-left">
                    <p className="text-[11px] uppercase tracking-widest text-slateline-400 font-semibold mb-3 text-center">
                      Workload
                    </p>

                    {/* Pause toggle */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="text-sm text-ink font-medium">
                          On pause
                        </Label>
                        <p className="text-xs text-slateline-400">
                          Skip me for new reviews
                        </p>
                      </div>
                      <Switch
                        checked={!!expert.is_paused}
                        onCheckedChange={togglePause}
                        data-testid="pause-toggle"
                      />
                    </div>

                    {/* Capacity input */}
                    <div>
                      <Label className="text-sm text-ink font-medium">
                        Weekly capacity
                      </Label>
                      <p className="text-xs text-slateline-400 mb-2">
                        Max concurrent in-flight reviews. Leave blank for no cap.
                      </p>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        defaultValue={expert.weekly_capacity ?? ""}
                        placeholder="No cap"
                        onBlur={(e) => {
                          const value = e.target.value === "" ? null : e.target.value;
                          if (value === null) {
                            // null = remove cap; backend's update_dict
                            // filter skips null so we need to clear via
                            // a sentinel. For now, just set to a high
                            // value that effectively means "no cap" —
                            // a proper PATCH semantics is a follow-up.
                            return;
                          }
                          if (parseInt(value, 10) !== (expert.weekly_capacity ?? -1)) {
                            updateWeeklyCapacity(value);
                          }
                        }}
                        className="w-full bg-canvas-card border border-slateline-200 rounded-sm px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand-green"
                        data-testid="weekly-capacity-input"
                      />
                    </div>
                  </div>
                </div>

                <nav className="space-y-2">
                  <button className="w-full flex items-center gap-3 px-4 py-3 bg-brand-greenPale/40 text-brand-green rounded-sm">
                    <TrendingUp className="w-5 h-5" />
                    Dashboard
                  </button>
                  <button 
                    onClick={() => {
                      const el = document.getElementById('slot-requests-section');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                    data-testid="slot-requests-btn"
                  >
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5" />
                      Slot Requests
                    </div>
                    {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length > 0 && (
                      <span className="bg-brand-green text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => navigate("/api-keys")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-brand-green hover:bg-brand-greenPale/40 rounded-sm transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                    Claude MCP Keys
                  </button>
                  <button
                    onClick={() => navigate("/expert/edit-profile")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-5 h-5" />
                    Edit Profile
                  </button>
                  <button 
                    onClick={() => navigate(`/experts/${expert.id}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                  >
                    <User className="w-5 h-5" />
                    View Public Profile
                  </button>
                  <a 
                    href="tel:+971558945236"
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-brand-green hover:bg-brand-greenPale/40 rounded-sm transition-colors"
                    data-testid="sidebar-support-btn"
                  >
                    <Headphones className="w-5 h-5" />
                    Support
                  </a>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slateline-500 hover:text-ink hover:bg-slateline-100 rounded-sm transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </nav>
                
                {/* Support Card */}
                <div className="mt-6 pt-6 border-t border-slateline-200">
                  <div className="bg-brand-greenPale/40 border border-brand-green/20 rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-brand-green" />
                      <span className="text-sm font-medium text-brand-green">Expert Support</span>
                    </div>
                    <a 
                      href="tel:+971558945236" 
                      className="text-ink hover:text-brand-greenHover transition-colors font-medium"
                    >
                      +971 55 894 5236
                    </a>
                    <p className="text-xs text-slateline-400 mt-1">Priority support for experts</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="font-display text-4xl mb-2">Expert Dashboard</h1>
                    <p className="text-slateline-500">Welcome back, {expert.name.split(' ')[0]}</p>
                  </div>
                  {expert.verified && (
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-brand-greenPale/60 text-brand-green rounded-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      Verified Expert
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-brand-green" />
                    <span className="text-slateline-500 text-sm">Total Earnings</span>
                  </div>
                  <p className="font-display text-3xl text-brand-green">${stats.total_earnings.toFixed(0)}</p>
                </div>
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Video className="w-5 h-5 text-brand-green" />
                    <span className="text-slateline-500 text-sm">Total Sessions</span>
                  </div>
                  <p className="font-display text-3xl">{stats.total_sessions}</p>
                </div>
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="text-slateline-500 text-sm">Rating</span>
                  </div>
                  <p className="font-display text-3xl">{stats.rating}</p>
                </div>
                <div className="bg-white border border-slateline-200 p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-brand-green" />
                    <span className="text-slateline-500 text-sm">Pending</span>
                  </div>
                  <p className="font-display text-3xl">{stats.pending_bookings}</p>
                </div>
              </motion.div>

              {/* Earnings Card */}
              <motion.div
                className="bg-gradient-to-r from-brand-greenPale/70 to-brand-greenPale/20 border border-brand-green/30 p-6 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-brand-greenPale/60 rounded-full flex items-center justify-center">
                      <Wallet className="w-7 h-7 text-brand-green" />
                    </div>
                    <div>
                      <p className="text-slateline-500 text-sm mb-1">Your Hourly Rate</p>
                      <p className="font-display text-4xl text-brand-green">${expert.hourly_rate}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slateline-500 text-sm mb-1">This Month</p>
                    <p className="font-display text-2xl text-ink">
                      ${pastBookings.filter(b => {
                        const bookingDate = new Date(b.scheduled_date);
                        const now = new Date();
                        return bookingDate.getMonth() === now.getMonth() && 
                               bookingDate.getFullYear() === now.getFullYear();
                      }).reduce((sum, b) => sum + (b.amount * 0.8), 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-slateline-400">After platform fee</p>
                  </div>
                </div>
              </motion.div>

              {/* BR-011 Tranche 1 — Active async reviews assigned to this
                  expert. THIS is the highest-priority block on the dashboard
                  — it represents work owed to a paid customer with a deadline.
                  Pinned above customer-bookings because the dashboard's job
                  is "tell the expert what they need to do next."

                  PERMANENT: renders even when empty so the section never
                  vanishes (the original gate `length > 0 && ...` caused the
                  block to disappear after the last review was opened, leaving
                  experts with no way to find async reviews on the dashboard).
                  Empty state explains how reviews land here for first-time
                  experts and reassures returning experts they're caught up. */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="mb-8 bg-white border border-slateline-200 p-5 sm:p-6 rounded-sm"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className="font-display text-2xl text-ink">
                    Active reviews
                  </h2>
                  <span className="text-xs text-slateline-400 tracking-wider uppercase">
                    {pendingReviews.length === 0
                      ? "Nothing pending"
                      : `${pendingReviews.length} assigned to you`}
                  </span>
                </div>
                <p className="text-sm text-slateline-500 mb-5">
                  Async reviews waiting for your verdict, sorted by deadline.
                </p>

                {pendingReviews.length === 0 ? (
                  <div className="flex items-start gap-4 p-5 rounded-sm border border-dashed border-slateline-200 bg-canvas/40">
                    <div className="w-10 h-10 rounded-full bg-brand-greenPale/60 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-brand-green" />
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-base text-ink mb-1">
                        You're all caught up.
                      </p>
                      <p className="text-sm text-slateline-500">
                        New async review assignments land here automatically —
                        you'll also get an email with a direct link to the brief.
                        Make sure your <span className="text-brand-green font-medium">review_categories</span> on
                        your profile cover the work you want to take.
                      </p>
                    </div>
                  </div>
                ) : (
                <ul className="space-y-3">
                    {pendingReviews.map((r) => {
                      const hrs = r.hours_remaining;
                      // Urgency tiers — drive border + label color
                      const urgent = hrs !== null && hrs < 4;
                      const soon   = hrs !== null && hrs >= 4 && hrs < 24;
                      const cool   = hrs !== null && hrs >= 24;
                      const overdue = hrs !== null && hrs <= 0;
                      return (
                        <li
                          key={r.review_id}
                          className={[
                            "flex flex-wrap items-center gap-3 rounded-sm border px-4 py-3.5 transition-colors",
                            overdue
                              ? "border-rose-300 bg-rose-50"
                              : urgent
                                ? "border-amber-300 bg-amber-50"
                                : soon
                                  ? "border-brand-green/30 bg-brand-greenPale/40"
                                  : "border-slateline-200 bg-canvas/40 hover:border-slateline-300",
                          ].join(" ")}
                        >
                          <div className="flex-1 min-w-[240px]">
                            <p className="font-display text-base text-ink flex items-center gap-2">
                              <span aria-hidden>{r.category_icon}</span>
                              <span>{r.artifact_name || `${r.category_label} review`}</span>
                            </p>
                            <p className="text-xs text-slateline-500 mt-0.5">
                              {r.client_name || "Customer"}
                              {r.category_label && ` · ${r.category_label}`}
                              {typeof r.n_attachments === "number" && r.n_attachments > 0 && (
                                <> · 📎 {r.n_attachments} file{r.n_attachments === 1 ? "" : "s"}</>
                              )}
                              {r.turnaround_hours && (<> · {r.turnaround_hours}h turnaround</>)}
                            </p>
                            {hrs !== null && (
                              <p className={[
                                "text-xs mt-1.5 font-medium flex items-center gap-1.5",
                                overdue ? "text-rose-700"
                                  : urgent ? "text-amber-700"
                                  : soon ? "text-brand-green"
                                  : "text-slateline-500",
                              ].join(" ")}>
                                {overdue ? <><AlertTriangle className="w-3.5 h-3.5" /> Overdue</>
                                  : urgent ? <><AlertTriangle className="w-3.5 h-3.5" /> {hrs.toFixed(1)}h left</>
                                  : soon ? <><Clock className="w-3.5 h-3.5" /> {hrs.toFixed(1)}h left</>
                                  : <><Clock className="w-3.5 h-3.5" /> {(hrs / 24).toFixed(1)} days left</>}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => navigate(r.brief_url)}
                              className="bg-brand-green hover:bg-brand-greenHover text-white text-sm rounded-sm"
                            >
                              <FileText className="w-4 h-4 mr-1.5" />
                              Open brief
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(r.verdict_url)}
                              className="border-slateline-300 text-slateline-600 hover:text-ink hover:border-ink/40 text-sm rounded-sm bg-white"
                            >
                              Submit verdict
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </motion.div>

              {/* BR-010 — My bookings (as a customer). Only renders if the
                  user has any. Pinned ABOVE the expert tabs because a
                  pending-payment block needs to be the first thing the
                  user sees on dashboard reload.

                  Styled to match the existing dashboard idiom: bg-white
                  card surface, rounded-sm (sharp corners — consistent
                  with the surrounding sections at lines 640/647/654/661),
                  brand-greenPale + brand-green for the "needs attention"
                  state instead of generic amber, so the warning lands
                  in the Xpert palette. */}
              {customerBookings.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mb-8 bg-white border border-slateline-200 p-5 sm:p-6 rounded-sm"
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <h2 className="font-display text-2xl text-ink">
                      My bookings
                    </h2>
                    <span className="text-xs text-slateline-400 tracking-wider uppercase">
                      As a customer
                    </span>
                  </div>
                  <p className="text-sm text-slateline-500 mb-5">
                    Sessions you've requested from other Xpert experts.
                  </p>

                  <ul className="space-y-3">
                    {customerBookings
                      // Pending-payment rows surface first so they're impossible to miss.
                      .slice()
                      .sort((a, b) => {
                        const aPending = a.payment_status === 'pending' && a.status !== 'cancelled' ? 0 : 1;
                        const bPending = b.payment_status === 'pending' && b.status !== 'cancelled' ? 0 : 1;
                        if (aPending !== bPending) return aPending - bPending;
                        return new Date(b.scheduled_date) - new Date(a.scheduled_date);
                      })
                      .map((b) => {
                        const isPending   = b.payment_status === 'pending' && b.status !== 'cancelled';
                        const isCancelled = b.status === 'cancelled';
                        return (
                          <li
                            key={b.id}
                            className={[
                              "flex flex-wrap items-center gap-3 rounded-sm border px-4 py-3.5 transition-colors",
                              isPending
                                // Brand-gold-pale = the "needs attention" idiom used elsewhere
                                // on the dashboard (sidebar promo at line 596, top banner at
                                // line 672). Visually consistent and on-palette.
                                ? "border-brand-green/30 bg-brand-greenPale/40"
                                : isCancelled
                                  ? "border-slateline-200 bg-slateline-50 opacity-70"
                                  : "border-slateline-200 bg-canvas/40 hover:border-slateline-300",
                            ].join(" ")}
                          >
                            <div className="flex-1 min-w-[200px]">
                              <p className="font-display text-base text-ink">
                                {b.expert_name || "Expert"}
                                {b.expert_anonymous && (
                                  <span className="text-slateline-400 text-xs ml-1 font-body normal-case">
                                    (anonymous)
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-slateline-500 mt-0.5">
                                {b.scheduled_date} · {b.scheduled_time}
                                {" · "}{b.duration_minutes} min
                                {" · "}<span className="text-ink font-semibold">${b.amount}</span>
                              </p>
                              {isPending && (
                                <p className="text-xs text-brand-green mt-2 flex items-center gap-1.5 font-medium">
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  Payment needed — your slot isn't locked until you pay.
                                </p>
                              )}
                              {isCancelled && (
                                <p className="text-xs text-slateline-500 mt-1.5 italic">Cancelled</p>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              {isPending && (
                                <Button
                                  size="sm"
                                  disabled={retryingCustomerPayment === b.id}
                                  onClick={() => handleCompleteCustomerPayment(b.id)}
                                  className="bg-brand-green hover:bg-brand-greenHover text-white text-sm rounded-sm"
                                >
                                  {retryingCustomerPayment === b.id ? (
                                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                  ) : (
                                    <DollarSign className="w-4 h-4 mr-1.5" />
                                  )}
                                  Complete payment
                                </Button>
                              )}
                              {b.meeting_link && b.payment_status === 'paid' && !isCancelled && (
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/meeting/${b.id}`)}
                                  className="bg-brand-green hover:bg-brand-greenHover text-white text-sm rounded-sm"
                                >
                                  <Video className="w-4 h-4 mr-1.5" />
                                  Join call
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/experts/${b.expert_id}`)}
                                className="border-slateline-300 text-slateline-600 hover:text-ink hover:border-ink/40 text-sm rounded-sm bg-white"
                              >
                                View expert
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                </motion.div>
              )}

              {/* Tabs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex gap-4 mb-6 border-b border-slateline-200">
                  <button
                    onClick={() => setActiveTab("upcoming")}
                    className={`pb-4 px-2 font-medium transition-colors relative ${
                      activeTab === "upcoming" 
                        ? "text-brand-green" 
                        : "text-slateline-500 hover:text-ink"
                    }`}
                  >
                    Upcoming Sessions ({upcomingBookings.length})
                    {activeTab === "upcoming" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-green" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("past")}
                    className={`pb-4 px-2 font-medium transition-colors relative ${
                      activeTab === "past" 
                        ? "text-brand-green" 
                        : "text-slateline-500 hover:text-ink"
                    }`}
                  >
                    Past Sessions ({pastBookings.length})
                    {activeTab === "past" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-green" />
                    )}
                  </button>
                </div>

                {/* Upcoming Sessions Tab */}
                {activeTab === "upcoming" && (
                  <>
                    {upcomingBookings.length > 0 ? (
                      <div className="space-y-4">
                        {upcomingBookings.map((booking) => (
                          <div 
                            key={booking.id}
                            className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
                          >
                            {/* Session Header - Clickable */}
                            <div 
                              className="p-6 cursor-pointer hover:bg-white/80 transition-colors"
                              onClick={() => setExpandedUpcoming(expandedUpcoming === booking.id ? null : booking.id)}
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-slateline-100 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-slateline-500" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold">Session with Client</h3>
                                    <div className="flex items-center gap-4 text-sm text-slateline-500 mt-1">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {booking.scheduled_time}
                                      </span>
                                      <span>{booking.duration_minutes} min</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-brand-green font-semibold">${booking.amount}</span>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                                    {booking.status}
                                  </span>
                                  {expandedUpcoming === booking.id ? (
                                    <ChevronUp className="w-5 h-5 text-slateline-500" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-slateline-500" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {expandedUpcoming === booking.id && (
                              <div className="border-t border-slateline-200 p-6 bg-canvas/50">
                                {/* Session Details Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                  <div className="flex items-center gap-3">
                                    <Calendar className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Date & Time</p>
                                      <p className="font-medium">
                                        {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                                          weekday: 'long',
                                          month: 'long',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                      <p className="text-sm text-slateline-500">{booking.scheduled_time}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Duration</p>
                                      <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <DollarSign className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Your Earnings</p>
                                      <p className="font-medium">${(booking.amount * 0.8).toFixed(0)}</p>
                                      <p className="text-xs text-slateline-400">After 20% platform fee</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Topic/Notes from Client */}
                                {booking.notes && (
                                  <div className="mb-6 p-4 bg-white rounded-sm border border-slateline-300">
                                    <p className="text-xs text-slateline-400 mb-1 flex items-center gap-2">
                                      <MessageSquare className="w-4 h-4" />
                                      Topic / Client's Notes
                                    </p>
                                    <p className="text-slateline-700">{booking.notes}</p>
                                  </div>
                                )}

                                {/* Expert's Notes */}
                                <div className="mb-6">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-slateline-400 flex items-center gap-2">
                                      <FileText className="w-4 h-4" />
                                      Your Notes
                                    </p>
                                    {editingNotes !== booking.id && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditingNotes(booking)}
                                        className="text-brand-green hover:text-brand-greenHover"
                                      >
                                        <Pencil className="w-4 h-4 mr-1" />
                                        {booking.expert_notes ? "Edit" : "Add Notes"}
                                      </Button>
                                    )}
                                  </div>
                                  
                                  {editingNotes === booking.id ? (
                                    <div className="space-y-3">
                                      <Textarea
                                        value={notesText}
                                        onChange={(e) => setNotesText(e.target.value)}
                                        placeholder="Add your preparation notes, talking points, or any relevant information..."
                                        className="bg-white border-slateline-300 min-h-[100px]"
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleSaveNotes(booking.id)}
                                          disabled={savingNotes}
                                          className="bg-brand-green hover:bg-brand-greenHover text-black"
                                        >
                                          {savingNotes ? (
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                          ) : (
                                            <Save className="w-4 h-4 mr-1" />
                                          )}
                                          Save Notes
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setEditingNotes(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="p-4 bg-white rounded-sm border border-slateline-300">
                                      {booking.expert_notes ? (
                                        <p className="text-slateline-700 whitespace-pre-wrap">{booking.expert_notes}</p>
                                      ) : (
                                        <p className="text-slateline-400 italic">No notes yet. Click "Add Notes" to prepare for this session.</p>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Join Meeting Button */}
                                <div className="flex flex-wrap gap-3">
                                  {/* AI Brief — shown for all MCP-booked sessions */}
                                  <Button
                                    variant="outline"
                                    className="border-brand-green/40 text-brand-greenHover hover:bg-brand-greenPale/40"
                                    onClick={() => navigate(`/expert/brief/${booking.id}`)}
                                  >
                                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                                    View AI Brief
                                  </Button>
                                  {booking.meeting_link || booking.daily_room_name ? (
                                    <Button
                                      onClick={() => navigate(`/meeting/${booking.id}`)}
                                      className="bg-brand-green hover:bg-brand-greenHover text-ink"
                                      data-testid={`join-meeting-${booking.id}`}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Join Video Call
                                    </Button>
                                  ) : booking.status === 'confirmed' && booking.payment_status === 'paid' ? (
                                    <Button
                                      onClick={() => navigate(`/meeting/${booking.id}`)}
                                      className="bg-brand-green hover:bg-brand-greenHover text-ink"
                                      data-testid={`join-meeting-${booking.id}`}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Join Video Call
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      className="border-slateline-300"
                                      onClick={() => navigate("/expert/edit-profile")}
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      Setup Meeting
                                    </Button>
                                  )}
                                  
                                  {/* Reschedule Button */}
                                  {booking.status !== 'cancelled' && !booking.reschedule_status && (
                                    <>
                                      <Button
                                        variant="outline"
                                        className="border-brand-green/50 text-brand-green hover:bg-brand-green/10"
                                        onClick={() => handleRequestUserReschedule(booking)}
                                        data-testid={`request-reschedule-${booking.id}`}
                                      >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Ask Client to Reschedule
                                      </Button>
                                      <Button
                                        variant="outline"
                                        className="border-brand-green/50 text-brand-green hover:bg-brand-green/10"
                                        onClick={() => setRescheduleModal(booking)}
                                        data-testid={`propose-reschedule-${booking.id}`}
                                      >
                                        <CalendarPlus className="w-4 h-4 mr-2" />
                                        Propose New Times
                                      </Button>
                                    </>
                                  )}
                                  
                                  {/* Reschedule status */}
                                  {booking.reschedule_status === 'pending' && (
                                    <span className="px-3 py-2 rounded bg-yellow-500/10 text-yellow-500 text-sm">
                                      Waiting for client to select new slot
                                    </span>
                                  )}
                                  {booking.reschedule_status === 'slots_proposed' && (
                                    <span className="px-3 py-2 rounded bg-blue-500/10 text-blue-500 text-sm">
                                      Slots sent - awaiting client selection
                                    </span>
                                  )}
                                  
                                  {/* Cancel Button */}
                                  {booking.status !== 'cancelled' && (
                                    <Button
                                      variant="outline"
                                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                      onClick={() => setCancelModal(booking)}
                                      data-testid={`cancel-${booking.id}`}
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Cancel Session
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white border border-slateline-200 p-12 rounded-sm text-center">
                        <Calendar className="w-12 h-12 text-slateline-500 mx-auto mb-4" />
                        <p className="text-slateline-500">No upcoming sessions yet</p>
                        <p className="text-sm text-slateline-400 mt-2">Share your profile to get bookings</p>
                      </div>
                    )}
                  </>
                )}

                {/* Past Sessions Tab */}
                {activeTab === "past" && (
                  <>
                    {pastBookings.length > 0 ? (
                      <div className="space-y-4">
                        {pastBookings.map((booking) => (
                          <div 
                            key={booking.id}
                            className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
                          >
                            {/* Session Header */}
                            <div 
                              className="p-6 cursor-pointer hover:bg-white/80 transition-colors"
                              onClick={() => setExpandedSession(expandedSession === booking.id ? null : booking.id)}
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-slateline-100 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-slateline-500" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold">Client Session</h3>
                                    <p className="text-sm text-slateline-400">
                                      {new Date(booking.scheduled_date).toLocaleDateString('en-US', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      })} • {booking.scheduled_time}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="text-brand-green font-semibold">${booking.amount}</p>
                                    <p className="text-xs text-slateline-400">
                                      Earned: ${(booking.amount * 0.8).toFixed(0)}
                                    </p>
                                  </div>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor('completed')}`}>
                                    Completed
                                  </span>
                                  {expandedSession === booking.id ? (
                                    <ChevronUp className="w-5 h-5 text-slateline-500" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-slateline-500" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {expandedSession === booking.id && (
                              <div className="border-t border-slateline-200 p-6 bg-canvas/50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                  <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Duration</p>
                                      <p className="font-medium">{formatDuration(booking.duration_minutes)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <DollarSign className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Your Earnings</p>
                                      <p className="font-medium">${(booking.amount * 0.8).toFixed(0)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Users className="w-5 h-5 text-brand-green" />
                                    <div>
                                      <p className="text-xs text-slateline-400">Platform Fee</p>
                                      <p className="font-medium">20% (${(booking.amount * 0.2).toFixed(0)})</p>
                                    </div>
                                  </div>
                                </div>

                                {booking.notes && (
                                  <div className="mb-6 p-4 bg-white rounded-sm">
                                    <p className="text-xs text-slateline-400 mb-1">Session Topic</p>
                                    <p className="text-sm text-slateline-700">{booking.notes}</p>
                                  </div>
                                )}

                                {/* Download Buttons */}
                                <div className="flex flex-wrap gap-3">
                                  <Button
                                    variant="outline"
                                    className="border-slateline-300 hover:bg-slateline-100"
                                    onClick={() => handleDownloadRecording(booking.id)}
                                    disabled={downloadingRecording === booking.id}
                                  >
                                    {downloadingRecording === booking.id ? (
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                      <Play className="w-4 h-4 mr-2" />
                                    )}
                                    Download Recording
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="border-slateline-300 hover:bg-slateline-100"
                                    onClick={() => handleDownloadTranscript(booking.id)}
                                    disabled={downloadingTranscript === booking.id}
                                  >
                                    {downloadingTranscript === booking.id ? (
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                      <FileText className="w-4 h-4 mr-2" />
                                    )}
                                    Download Transcript
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white border border-slateline-200 p-12 rounded-sm text-center">
                        <Video className="w-12 h-12 text-slateline-500 mx-auto mb-4" />
                        <p className="text-slateline-500">No past sessions yet</p>
                        <p className="text-sm text-slateline-400 mt-2">Completed sessions will appear here</p>
                      </div>
                    )}
                  </>
                )}
              </motion.div>

              {/* Confidentiality records (per-booking signed acknowledgements) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <ExpertLegalRecords />
              </motion.div>

              {/* Payouts setup (Stripe Connect — Phase A) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.27 }}
                className="bg-white border border-slateline-200 rounded-sm p-6"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-display text-xl mb-1">Payouts</h2>
                    <p className="text-sm text-slateline-400">
                      Connect a bank account to receive payments from completed sessions.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate("/expert/payouts/setup")}
                    className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md whitespace-nowrap"
                  >
                    Set up payouts →
                  </Button>
                </div>
              </motion.div>

              {/* Slot Requests Section */}
              <motion.div
                id="slot-requests-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white border border-slateline-200 rounded-sm overflow-hidden"
              >
                <div className="p-6 border-b border-slateline-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-brand-green" />
                      <h2 className="font-display text-xl">Slot Requests</h2>
                      {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length > 0 && (
                        <span className="bg-brand-green text-white text-xs font-bold px-2 py-1 rounded-full">
                          {slotRequests.filter(r => r.status === 'pending' || r.status === 'more_requested').length} new
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slateline-400 mt-1">Users requesting time slots from you</p>
                </div>

                <div className="divide-y divide-slate-800">
                  {loadingRequests ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-brand-green mx-auto" />
                    </div>
                  ) : slotRequests.length === 0 ? (
                    <div className="p-12 text-center">
                      <CalendarPlus className="w-12 h-12 text-slateline-500 mx-auto mb-4" />
                      <p className="text-slateline-500">No slot requests yet</p>
                      <p className="text-sm text-slateline-400 mt-2">When users request time slots, they'll appear here</p>
                    </div>
                  ) : (
                    slotRequests.map((request) => {
                      const statusBadge = getStatusBadge(request.status);
                      return (
                        <div key={request.id} className="p-4 hover:bg-slateline-100/30 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-slateline-200 flex items-center justify-center">
                                  <User className="w-5 h-5 text-slateline-500" />
                                </div>
                                <div>
                                  <p className="font-medium text-ink">{request.user_name}</p>
                                  <p className="text-sm text-slateline-400">{request.user_email}</p>
                                </div>
                              </div>
                              <div className="ml-13 space-y-1">
                                <p className="text-sm text-slateline-500">
                                  <span className="text-slateline-400">Requested:</span>{" "}
                                  {request.timeframe === '1_week' ? 'Within next week' : 'Within next 2 weeks'}
                                </p>
                                <p className="text-sm text-slateline-500">
                                  <span className="text-slateline-400">Duration:</span>{" "}
                                  {request.duration_minutes} minutes
                                </p>
                                <p className="text-sm text-slateline-500">
                                  <span className="text-slateline-400">Timezone:</span>{" "}
                                  {request.user_timezone}
                                </p>
                                {request.message && (
                                  <p className="text-sm text-slateline-500 italic">"{request.message}"</p>
                                )}
                                {request.user_message && (
                                  <p className="text-sm text-amber-600 mt-2">
                                    <span className="text-slateline-400">User's response:</span> "{request.user_message}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                              <span className="text-xs text-slateline-400">
                                {new Date(request.created_at).toLocaleDateString()}
                              </span>
                              {(request.status === 'pending' || request.status === 'more_requested') && (
                                <Button
                                  size="sm"
                                  className="bg-brand-green hover:bg-brand-greenHover text-white mt-2"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setProposedSlots([]);
                                    setExpertMessage("");
                                    setSelectedProposalDate(null);
                                    // Auto-detect expert's timezone
                                    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                    const tzInList = TIMEZONES.find(tz => tz.value === userTz);
                                    setSelectedTimezone(tzInList ? userTz : "UTC");
                                  }}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Propose Slots
                                </Button>
                              )}
                              {request.status === 'slots_proposed' && request.proposed_slots && (
                                <div className="text-right mt-2">
                                  <p className="text-xs text-slateline-400 mb-1">Proposed:</p>
                                  {request.proposed_slots.slice(0, 2).map((slot, i) => (
                                    <p key={i} className="text-xs text-blue-400">
                                      {slot.date} {slot.time_12h}
                                    </p>
                                  ))}
                                  {request.proposed_slots.length > 2 && (
                                    <p className="text-xs text-slateline-400">+{request.proposed_slots.length - 2} more</p>
                                  )}
                                </div>
                              )}
                              {request.status === 'accepted' && request.accepted_slot && (
                                <div className="text-right mt-2">
                                  <p className="text-xs text-brand-green mb-1">Accepted:</p>
                                  <p className="text-xs text-brand-green">
                                    {request.accepted_slot.date} {request.accepted_slot.time_12h}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>

              {/* Propose Slots Modal */}
              {selectedRequest && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white border border-slateline-300 rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto"
                  >
                    <div className="p-6 border-b border-slateline-300">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-xl">Propose Time Slots</h3>
                        <button
                          onClick={() => {
                            setSelectedRequest(null);
                            setSelectedProposalDate(null);
                          }}
                          className="text-slateline-500 hover:text-ink"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-sm text-slateline-400 mt-1">
                        For {selectedRequest.user_name} • {selectedRequest.duration_minutes} min session
                      </p>
                      <p className="text-xs text-slateline-500 mt-1">
                        User's timezone: {selectedRequest.user_timezone}
                      </p>
                    </div>

                    <div className="p-6 space-y-5">
                      {/* Step 1: Select Timezone */}
                      <div>
                        <label className="text-sm font-medium text-slateline-700 mb-2 block flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">1</span>
                          Select Your Timezone
                        </label>
                        <select
                          value={selectedTimezone}
                          onChange={(e) => setSelectedTimezone(e.target.value)}
                          className="w-full p-3 bg-slateline-100 border border-slateline-300 rounded-lg text-ink focus:border-brand-green focus:outline-none"
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Step 2: Select Date */}
                      <div>
                        <label className="text-sm font-medium text-slateline-700 mb-2 block flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">2</span>
                          Select Date
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                          {generateAvailableDates().map((dateObj, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedProposalDate(dateObj.date)}
                              className={`p-2 text-sm rounded border transition-colors ${
                                selectedProposalDate === dateObj.date
                                  ? 'bg-brand-greenPale/60 border-brand-green text-brand-green'
                                  : 'bg-slateline-100 border-slateline-300 text-slateline-700 hover:border-brand-green/40'
                              }`}
                            >
                              <div className="font-medium">{dateObj.displayDate}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Step 3: Select Time Slots */}
                      {selectedProposalDate && (
                        <div>
                          <label className="text-sm font-medium text-slateline-700 mb-2 block flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-brand-green text-white text-xs flex items-center justify-center font-bold">3</span>
                            Select Time Slots for {new Date(selectedProposalDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </label>
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {generateTimeSlotsForDate(selectedProposalDate).map((slot, i) => {
                              const isSelected = proposedSlots.find(s => s.date === slot.date && s.time === slot.time);
                              return (
                                <button
                                  key={i}
                                  onClick={() => isSelected 
                                    ? setProposedSlots(proposedSlots.filter(s => !(s.date === slot.date && s.time === slot.time)))
                                    : addProposedSlot(slot.date, slot.time, slot.time_12h)
                                  }
                                  className={`p-2 text-sm rounded border transition-colors ${
                                    isSelected
                                      ? 'bg-brand-greenPale/60 border-brand-green text-brand-green'
                                      : 'bg-slateline-100 border-slateline-300 text-slateline-700 hover:border-brand-green/40'
                                  }`}
                                >
                                  {slot.time_12h}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-slateline-400 mt-2">
                            Times shown in {TIMEZONES.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                          </p>
                        </div>
                      )}

                      {/* Selected slots preview */}
                      {proposedSlots.length > 0 && (
                        <div className="bg-slateline-100/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slateline-700">
                              Selected Slots ({proposedSlots.length}/6)
                            </label>
                            {proposedSlots.length >= 6 && (
                              <span className="text-xs text-amber-600">Maximum reached</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {proposedSlots.map((slot, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-greenPale/60 text-brand-green text-sm rounded-full"
                              >
                                {slot.date} @ {slot.time_12h}
                                <button onClick={() => removeProposedSlot(i)} className="hover:text-ink ml-1">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Message to user */}
                      <div>
                        <label className="text-sm font-medium text-slateline-700 mb-2 block">
                          Message to User (optional)
                        </label>
                        <Textarea
                          value={expertMessage}
                          onChange={(e) => setExpertMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          className="bg-slateline-100 border-slateline-300"
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="p-6 border-t border-slateline-300 flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSelectedRequest(null);
                          setSelectedProposalDate(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-brand-green hover:bg-brand-greenHover text-white"
                        onClick={() => handleProposeSlots(selectedRequest.id)}
                        disabled={proposingSlots || proposedSlots.length === 0}
                      >
                        {proposingSlots ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send {proposedSlots.length} Slot{proposedSlots.length !== 1 ? 's' : ''}
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Cancel Booking Modal */}
      <Dialog open={!!cancelModal} onOpenChange={() => { setCancelModal(null); setCancelReason(""); }}>
        <DialogContent className="bg-white border-slateline-300">
          <DialogHeader>
            <DialogTitle className="text-ink flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancel Session
            </DialogTitle>
            <DialogDescription className="text-slateline-500">
              Cancelling this session will issue a full refund to the client.
            </DialogDescription>
          </DialogHeader>
          
          {cancelModal && (
            <div className="space-y-4">
              <div className="bg-slateline-100/50 rounded-lg p-4">
                <p className="text-ink font-medium">{cancelModal.user_name}</p>
                <p className="text-slateline-500 text-sm">
                  {cancelModal.scheduled_date} at {cancelModal.scheduled_time}
                </p>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-500 font-medium mb-1">Full Refund Policy</p>
                <p className="text-slateline-700 text-sm">
                  When you cancel a session, the client receives a 100% refund (${cancelModal.amount?.toFixed(2)})
                </p>
              </div>
              
              <div>
                <label className="text-sm text-slateline-500 mb-2 block">Reason for cancellation (optional)</label>
                <Textarea
                  placeholder="Let the client know why you're cancelling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="bg-slateline-100 border-slateline-300 text-ink"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setCancelModal(null); setCancelReason(""); }}
              className="border-slateline-300"
            >
              Keep Session
            </Button>
            <Button
              onClick={handleCancelBooking}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-500 text-ink"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel & Refund"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Propose Reschedule Slots Modal */}
      <Dialog open={!!rescheduleModal} onOpenChange={() => { setRescheduleModal(null); setRescheduleSlots([]); setRescheduleMessage(""); setRescheduleDate(null); }}>
        <DialogContent className="bg-white border-slateline-300 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-ink">Propose New Time Slots</DialogTitle>
            <DialogDescription className="text-slateline-500">
              Select up to 6 alternative time slots for {rescheduleModal?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          {rescheduleModal && (
            <div className="space-y-4">
              {/* Current session info */}
              <div className="bg-slateline-100/50 rounded-lg p-4">
                <p className="text-sm text-slateline-500">Current Session</p>
                <p className="text-ink">
                  {rescheduleModal.scheduled_date} at {rescheduleModal.scheduled_time}
                </p>
              </div>
              
              {/* Date selection */}
              <div>
                <label className="text-sm font-medium text-slateline-700 mb-2 block">Select Date</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {generateAvailableDates().map((dateInfo) => (
                    <button
                      key={dateInfo.date}
                      onClick={() => setRescheduleDate(dateInfo.date)}
                      className={`p-2 text-center rounded border transition-colors ${
                        rescheduleDate === dateInfo.date
                          ? 'bg-brand-greenPale/60 border-brand-green text-brand-green'
                          : 'bg-slateline-100 border-slateline-300 hover:border-brand-green/40'
                      }`}
                    >
                      <div className="text-xs text-slateline-500">{dateInfo.dayName.slice(0, 3)}</div>
                      <div className="font-medium">{dateInfo.displayDate.split(',')[0].split(' ')[1]}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Time slots */}
              {rescheduleDate && (
                <div>
                  <label className="text-sm font-medium text-slateline-700 mb-2 block">Select Time Slots</label>
                  <div className="grid grid-cols-4 gap-2">
                    {generateTimeSlotsForDate(rescheduleDate).map((slot) => {
                      const isSelected = rescheduleSlots.find(s => s.date === slot.date && s.time === slot.time);
                      return (
                        <button
                          key={`${slot.date}-${slot.time}`}
                          onClick={() => isSelected 
                            ? setRescheduleSlots(rescheduleSlots.filter(s => !(s.date === slot.date && s.time === slot.time)))
                            : addRescheduleSlot(slot.date, slot.time, slot.time_12h)
                          }
                          className={`p-2 text-sm rounded border transition-colors ${
                            isSelected
                              ? 'bg-brand-greenPale/60 border-brand-green text-brand-green'
                              : 'bg-slateline-100 border-slateline-300 text-slateline-700 hover:border-brand-green/40'
                          }`}
                        >
                          {slot.time_12h}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Selected slots preview */}
              {rescheduleSlots.length > 0 && (
                <div className="bg-slateline-100/50 rounded-lg p-4">
                  <label className="text-sm font-medium text-slateline-700 mb-2 block">
                    Selected Slots ({rescheduleSlots.length}/6)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {rescheduleSlots.map((slot, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-greenPale/60 text-brand-green text-sm rounded-full"
                      >
                        {slot.date} @ {slot.time_12h}
                        <button onClick={() => removeRescheduleSlot(i)} className="hover:text-ink ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Message */}
              <div>
                <label className="text-sm font-medium text-slateline-700 mb-2 block">Message (optional)</label>
                <Textarea
                  value={rescheduleMessage}
                  onChange={(e) => setRescheduleMessage(e.target.value)}
                  placeholder="Add a message explaining the reschedule..."
                  className="bg-slateline-100 border-slateline-300"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setRescheduleModal(null); setRescheduleSlots([]); setRescheduleMessage(""); setRescheduleDate(null); }}
              className="border-slateline-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleProposeRescheduleSlots}
              disabled={proposingReschedule || rescheduleSlots.length === 0}
              className="bg-brand-green hover:bg-brand-greenHover text-black"
              data-testid="send-reschedule-slots-btn"
            >
              {proposingReschedule ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Slots
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
